import React from 'react';
import {
  useInvalidateQueries,
  useUpdateRecord,
  useCreateRecord,
} from 'src/api/APIHooks';
import { defaultMutationConfig } from 'common/utils/queryDefaults';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import { MultiSelectField, SelectField } from '@unite-us/app-components';
import ModalEditFormSubmitState from 'common/modal/ModalEditFormSubmitState';

export const useUpdateCase = () => {
  const invalidateQueries = useInvalidateQueries();
  return useUpdateRecord('case', {
    mutationConfig: {
      onSuccess: () => {
        invalidateQueries('cases');
      },
    },
  });
};

const useCreateNote = () => {
  const invalidateQueries = useInvalidateQueries();
  return useCreateRecord('note', {
    mutationConfig: {
      onSuccess: () => {
        invalidateQueries('notes');
      },
      ...defaultMutationConfig,
    },
  });
};

const ClientNeedForm = ({
  closeModal,
  selectedConditionIds,
  conditions,
  caseId,
  formId,
  modalIsOpen,
  onSubmitStateChange,
  reloadCase,
  currentEmployeeId,
  multipleClientNeedsFlag,
}) => {
  const { updateRecord: updateCase } = useUpdateCase();
  const { createRecord: createNote } = useCreateNote();

  const personConditions = conditions.map((condition) => ({
    label: condition.name,
    value: condition.id,
  }));

  const onSubmit = async (formInputs) => {
    let newSelectedIds;
    if (multipleClientNeedsFlag) {
      newSelectedIds = formInputs.client_needs || [];
    } else {
      newSelectedIds = formInputs.client_needs ? [formInputs.client_needs] : [];
    }
    const hasChanges = JSON.stringify(selectedConditionIds.sort()) !== JSON.stringify(newSelectedIds.sort());

    if (hasChanges) {
      const originalClientNeeds = selectedConditionIds
        .map((id) => conditions.find((condition) => condition.id === id)?.name)
        .join(', ');
      const newClientNeeds = newSelectedIds
        .map((id) => conditions.find((condition) => condition.id === id)?.name)
        .join(', ');

      await Promise.all([
        updateCase(caseId, {
          person_conditions: newSelectedIds.map((id) => ({
            id,
            type: 'person_condition',
          })),
        }),
        createNote({
          text: `Associated needs changed from ${originalClientNeeds} to ${newClientNeeds}`,
          subjectType: 'case',
          subject: caseId,
          employee: currentEmployeeId,
          category: 'general',
          details: { deletable: false, type: 'update' },
        }),
      ]).then(() => {
        reloadCase();
      });
    }
    closeModal();
  };

  if (!modalIsOpen) {
    return null;
  }

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{ client_needs: multipleClientNeedsFlag ? selectedConditionIds : selectedConditionIds[0] }}
      render={({ handleSubmit, submitting, values }) => {
        const isSubmitDisabled = multipleClientNeedsFlag ?
          (submitting || !values.client_needs || values.client_needs.length === 0) :
          (submitting || !values.client_needs);

        return (
          <form id={formId} className="mt-4" onSubmit={handleSubmit}>
            <ModalEditFormSubmitState
              onChange={onSubmitStateChange}
              submitting={submitting}
              submitDisabled={isSubmitDisabled}
            />
            <div style={{ marginBottom: '115px' }}>
              <Field
                name="client_needs"
                validate={(value) => {
                  if (multipleClientNeedsFlag) {
                    if (!value || value.length === 0) {
                      return 'Required';
                    }
                  } else if (!value) {
                    return 'Required';
                  }
                  return undefined;
                }}
                render={({ input, meta }) => (
                  multipleClientNeedsFlag ? (
                    <MultiSelectField
                      id="service-case-reason"
                      label="Associated Needs"
                      options={personConditions}
                      value={input.value || []}
                      onChange={input.onChange}
                      onBlur={input.onBlur}
                      required
                      placeholder="Pick One or More"
                      clearable
                      error={meta.touched && meta.error}
                    />
                  ) : (
                    <SelectField
                      id="service-case-reason"
                      label="Associated Need"
                      options={personConditions}
                      value={input.value}
                      onChange={input.onChange}
                      onBlur={input.onBlur}
                      required
                      placeholder="Pick One"
                      clearable
                      error={meta.touched && meta.error}
                    />
                  )
                )}
              />
            </div>
          </form>
        );
      }}
    />
  );
};

ClientNeedForm.propTypes = {
  closeModal: PropTypes.func.isRequired,
  selectedConditionIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  conditions: PropTypes.array.isRequired,
  caseId: PropTypes.string.isRequired,
  formId: PropTypes.string.isRequired,
  modalIsOpen: PropTypes.bool.isRequired,
  onSubmitStateChange: PropTypes.func.isRequired,
  reloadCase: PropTypes.func.isRequired,
  currentEmployeeId: PropTypes.string.isRequired,
  multipleClientNeedsFlag: PropTypes.bool,
};

ClientNeedForm.defaultProps = {
  multipleClientNeedsFlag: false,
};

export default ClientNeedForm;
